import styled, { css } from 'styled-components';
import { media, layout, colors } from '@prisjakt/ui-base';
import { Card as CardComponent, CardActionArea } from '@prisjakt/ui/card';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import PriceNotice from '../components/price-notice';
import StockStatusColumnComponent from '../components/stock-status-column';
import CampaignBadgeComponent from '../../../../../../components/campaign-badge';
import { ITableLinkProps } from './types';

const stockIconWidth = '40px';

export interface IPromoted {
  isPromoted: boolean;
}

export const OfferCard = styled(CardComponent)<IPromoted>`
  border-radius: 4px;
  background: ${props => props.theme.buttons.category.bg};
  ${props => {
    return props.isPromoted
      ? `box-shadow: 0 0 0 1px ${props.theme.buttons.primary.bg}`
      : '';
  }}
`;

export const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 80px;
`;

export const RowColumn = styled.div`
  display: flex;
  align-self: stretch;
  margin: 0 0.5rem;
`;

export const ActionArea = styled(CardActionArea)`
  flex: 1 1 auto;
  cursor: pointer;
`;

export const AdColumnContent = styled.div`
  transform: rotate(-90deg);
  color: ${props => props.theme.buttons.primary.text};
  position: absolute;
  bottom: -24px;
  left: 2px;
  background: none;
  transform-origin: 0 0;
  width: 80px;
  height: 1.5rem;
  font-size: 0.9em;
  text-align: center;
`;

export const StyledAdColumn = styled.div`
  display: flex;
  align-self: stretch;
  position: relative;
  border-radius: 4px 0 0 4px;
  background: ${props => props.theme.buttons.primary.bg};
  width: 1.5rem;
  margin-left: -1px;
`;

type ColumnProps = {
  expand?: boolean;
  expandOnMobile?: boolean;
};

export const Column = styled.div<ColumnProps>(
  props => css`
    display: flex;
    align-items: stretch;

    > .pj-ui-popover--wrapper {
      display: block;
      width: 100%;
      height: 100%;
    }

    ${props.expandOnMobile &&
      media.mediumDown`
      width: 100%;
    `}

    ${props.expand &&
      css`
        flex: 2;
      `}
  `
);

interface LinkProps extends ITableLinkProps {
  alignCenter?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  vertical?: boolean;
}

export const Link = styled.a<LinkProps>(
  props => css`
  
  display: flex;
  flex: 2;
  padding: 0.75rem ${layout.spacing.xSmall};
  align-items: center;

  ${!props.featured &&
    css`
      cursor: default;
    `}

  ${props.alignCenter &&
    css`
      justify-content: center;
    `}
  ${props.alignLeft &&
    css`
      justify-content: flex-start;
    `}
  ${props.alignRight &&
    css`
      justify-content: flex-end;
    `}
    
    &:target {
      outline: ${colors.borderLightGray} dotted 1px;
      outline: -webkit-focus-ring-color auto 1px;
    }
`
);

export const GoToStoreLink = styled(Link)`
  padding: 0.75rem ${layout.spacing.small} 0.75rem ${layout.spacing.xSmall};
`;

type SharedRowProps = {
  noExternalUri?: boolean;
  featured?: boolean;
  showReport?: boolean;
};

const rowStyles = css`
  line-height: 1.3em;
  position: relative;

  ${(props: SharedRowProps) =>
    props.noExternalUri &&
    css`
      cursor: default;
    `}

  ${props =>
    !props.featured &&
    css`
      color: ${props.theme.text.secondary};
      &:hover {
        color: ${props.theme.text.secondary};
      }
      background: ${props.theme.colors.base100};
    `}
`;

type RowExperimentProps = {
  expanded?: boolean;
};

export const LeftColumn = styled.div`
  align-items: stretch;
`;

export const MiddleColumn = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    flex: 1;
    border-left: ${props.featured
      ? `1px solid ${props.theme.colors.base200}`
      : 'none'};
    ${props.featured &&
      css`
        &:hover::after {
          box-shadow: 0 2px 10px 0 rgb(0, 0, 0, 0.1), 0 0 0 1px rgb(0, 0, 0, 0);
        }
        ::after {
          transition: box-shadow 200ms ease;
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          pointer-events: none;
          border-radius: 8px;
        }
      `}
  `
);

export const RowWrapper = styled.div<SharedRowProps & RowExperimentProps>(
  props => css`
    ${rowStyles}
    width: 100%;
    ${props.featured &&
      css`
        box-shadow: 0 0 0 1px ${props.theme.colors.base200};
      `}

    border-radius: 8px;
    margin-bottom: ${layout.spacing.xSmall};

    ${props.expanded &&
      css`
        box-shadow: 0 0 0 1px ${props.theme.colors.base200};
      `}
  `
);

export const Row = styled.div<SharedRowProps & RowExperimentProps>(
  props => css`
    ${rowStyles}
    height: ${props.featured ? '80px' : '54px'};
    border-radius: 8px 8px 0 0;
    display: flex;

    ${props.expanded &&
      css`
        border-bottom: 1px solid ${props.theme.colors.base200};
      `}
  `
);

export const InfoRow = styled.div`
  ${rowStyles}
  background-color: rgba(0, 0, 0, 0.03);
`;

export const AltRow = styled.div`
  ${rowStyles}
  display: flex;
  flex: 1;
  background-color: rgba(0, 0, 0, 0.03);
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  padding: ${layout.spacing.xSmall} 0;
`;

type ReportRowProps = {
  isAlternative?: boolean;
};

export const ReportRow = styled.div<ReportRowProps>`
  ${rowStyles}
  top: ${p => (p.isAlternative ? 0 : -layout.spacing.rem.xSmall)}rem;
  padding: 0 ${layout.spacing.small};
`;

export const AltReportRow = styled.div`
  ${rowStyles}
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0 ${layout.spacing.small};
`;

type ProductNameProps = {
  light?: boolean;
};

export const ProductName = styled(BodySmallText)<ProductNameProps>`
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${props =>
    props.light ? props.theme.text.tertiary : props.theme.text.primary};
  ${media.smallOnly`
    display: none;  
  `};
`;

export const TransparencyShopInfo = styled.div`
  text-align: left;
  margin: 0;
`;

export const ProductNameColumn = styled.div`
  display: flex;
  align-items: center;
  ${media.mediumUp`
    max-width: 72%;
  `}
`;

export const PriceAndStockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  ${media.smallOnly`
    display: grid;
    grid-template-columns: ${stockIconWidth} 1fr;
    align-items: baseline;
    
  `}
`;

export const PriceNoticeLabel = styled(PriceNotice)`
  font-size: ${layout.measurements.fontSizeSmall};
  white-space: nowrap;
  font-style: italic;
  margin-bottom: 2px;
`;

const sharedPriceColumnStyles = css`
  display: flex;
  align-items: center;
  height: 24px;
`;

export const PriceAndStockColumn = styled.div`
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  align-items: flex-end;
`;

export const PriceColumnWrapper = styled.div`
  ${sharedPriceColumnStyles};
`;

export const PriceColumnWrapperExperiment = styled.div`
  ${sharedPriceColumnStyles};

  ${media.mediumUp`
    display: none;
  `}
`;

export const StockStatusColumn = styled(StockStatusColumnComponent)`
  ${sharedPriceColumnStyles};
`;

export const CampaignBadge = styled(CampaignBadgeComponent)`
  margin-bottom: 2px;
`;
